import { useMemo } from "react";
import { AlertTriangle, TrendingDown, Shield, ChevronDown, ChevronUp, Info } from "lucide-react";
import { useState } from "react";

/**
 * StressTest Component
 * Shows how a deal performs under adverse conditions
 * Addresses pain point: "30-year charts are pretty graphs, not decision tools"
 */
export default function StressTest({ inputs, baseAnalysis }) {
  const [expanded, setExpanded] = useState(true);

  // Calculate stress scenarios
  const scenarios = useMemo(() => {
    if (!inputs || !baseAnalysis) return null;

    const {
      purchase_price = 0,
      monthly_rent = 0,
      down_payment_percent = 20,
      interest_rate = 7,
      loan_term_years = 30,
      vacancy_rate = 5,
      maintenance_percent = 5,
      capex_percent = 5,
      management_percent = 10,
      property_taxes_annual = 0,
      insurance_annual = 0,
    } = inputs;

    // Helper to calculate monthly mortgage
    const calcMortgage = (principal, rate, years) => {
      const monthlyRate = (rate / 100) / 12;
      const numPayments = years * 12;
      if (monthlyRate === 0) return principal / numPayments;
      return principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / 
             (Math.pow(1 + monthlyRate, numPayments) - 1);
    };

    // Helper to calculate cash flow
    const calcCashFlow = (rent, vacancyPct, maintPct, capexPct, mgmtPct, mortgage, taxes, insurance) => {
      const effectiveRent = rent * (1 - vacancyPct / 100);
      const maintenance = rent * (maintPct / 100);
      const capex = rent * (capexPct / 100);
      const management = rent * (mgmtPct / 100);
      const monthlyTaxes = taxes / 12;
      const monthlyInsurance = insurance / 12;
      const totalExpenses = maintenance + capex + management + monthlyTaxes + monthlyInsurance;
      return effectiveRent - totalExpenses - mortgage;
    };

    const downPayment = purchase_price * (down_payment_percent / 100);
    const loanAmount = purchase_price - downPayment;

    // Base case
    const baseMortgage = calcMortgage(loanAmount, interest_rate, loan_term_years);
    const baseCashFlow = calcCashFlow(
      monthly_rent, vacancy_rate, maintenance_percent, capex_percent, 
      management_percent, baseMortgage, property_taxes_annual, insurance_annual
    );

    // Scenario 1: Interest Rate +2%
    const rateShockMortgage = calcMortgage(loanAmount, interest_rate + 2, loan_term_years);
    const rateShockCashFlow = calcCashFlow(
      monthly_rent, vacancy_rate, maintenance_percent, capex_percent,
      management_percent, rateShockMortgage, property_taxes_annual, insurance_annual
    );

    // Scenario 2: High Vacancy (20%)
    const highVacancyCashFlow = calcCashFlow(
      monthly_rent, 20, maintenance_percent, capex_percent,
      management_percent, baseMortgage, property_taxes_annual, insurance_annual
    );

    // Scenario 3: Major Repair Year ($15K spread over 12 months impact)
    const majorRepairMonthlyCost = 15000 / 12;
    const majorRepairCashFlow = baseCashFlow - majorRepairMonthlyCost;

    // Scenario 4: Market Downturn (rent -15%, vacancy +10%)
    const downturnRent = monthly_rent * 0.85;
    const downturnCashFlow = calcCashFlow(
      downturnRent, vacancy_rate + 10, maintenance_percent, capex_percent,
      management_percent, baseMortgage, property_taxes_annual, insurance_annual
    );

    // Scenario 5: Perfect Storm (rate +2%, vacancy 15%, rent -10%)
    const stormMortgage = calcMortgage(loanAmount, interest_rate + 2, loan_term_years);
    const stormRent = monthly_rent * 0.90;
    const stormCashFlow = calcCashFlow(
      stormRent, 15, maintenance_percent, capex_percent,
      management_percent, stormMortgage, property_taxes_annual, insurance_annual
    );

    // Calculate survival metrics
    const cashReserveNeeded = Math.max(0, -stormCashFlow * 12); // 1 year of negative cash flow
    
    return {
      base: {
        cashFlow: baseCashFlow,
        annual: baseCashFlow * 12
      },
      rateShock: {
        name: "Interest Rate +2%",
        description: `If rates rise to ${(interest_rate + 2).toFixed(1)}%`,
        cashFlow: rateShockCashFlow,
        impact: rateShockCashFlow - baseCashFlow,
        survives: rateShockCashFlow > -200
      },
      highVacancy: {
        name: "High Vacancy (20%)",
        description: "Extended vacancy period or difficult tenant market",
        cashFlow: highVacancyCashFlow,
        impact: highVacancyCashFlow - baseCashFlow,
        survives: highVacancyCashFlow > -200
      },
      majorRepair: {
        name: "Major Repair Year",
        description: "$15K unexpected repair (roof, HVAC, foundation)",
        cashFlow: majorRepairCashFlow,
        impact: majorRepairCashFlow - baseCashFlow,
        survives: majorRepairCashFlow > -500
      },
      marketDownturn: {
        name: "Market Downturn",
        description: "Rent drops 15%, vacancy increases 10%",
        cashFlow: downturnCashFlow,
        impact: downturnCashFlow - baseCashFlow,
        survives: downturnCashFlow > -300
      },
      perfectStorm: {
        name: "Perfect Storm",
        description: "Rate +2%, rent -10%, vacancy 15%",
        cashFlow: stormCashFlow,
        impact: stormCashFlow - baseCashFlow,
        survives: stormCashFlow > -500
      },
      cashReserveNeeded,
      overallScore: [
        rateShockCashFlow > -200,
        highVacancyCashFlow > -200,
        majorRepairCashFlow > -500,
        downturnCashFlow > -300,
        stormCashFlow > -500
      ].filter(Boolean).length
    };
  }, [inputs, baseAnalysis]);

  if (!scenarios) return null;

  const formatCurrency = (value) => {
    const prefix = value < 0 ? '-' : '';
    return prefix + '$' + Math.abs(Math.round(value)).toLocaleString();
  };

  const getScoreColor = (score) => {
    if (score >= 4) return 'text-emerald-500';
    if (score >= 2) return 'text-amber-500';
    return 'text-red-500';
  };

  const getScoreLabel = (score) => {
    if (score >= 4) return 'Resilient';
    if (score >= 2) return 'Moderate Risk';
    return 'High Risk';
  };

  const scenarioList = [
    scenarios.rateShock,
    scenarios.highVacancy,
    scenarios.majorRepair,
    scenarios.marketDownturn,
    scenarios.perfectStorm
  ];

  return (
    <div className="glass-card rounded-xl p-6 mt-6" data-testid="stress-test-panel">
      {/* Header */}
      <button 
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between mb-4"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-500/10">
            <Shield className="h-5 w-5 text-amber-500" />
          </div>
          <div className="text-left">
            <h3 className="text-lg font-semibold text-white">Stress Test</h3>
            <p className="text-sm text-slate-400">How does this deal survive worst-case scenarios?</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className={`text-2xl font-bold ${getScoreColor(scenarios.overallScore)}`}>
            {scenarios.overallScore}/5
          </div>
          <span className={`text-sm px-2 py-1 rounded ${
            scenarios.overallScore >= 4 ? 'bg-emerald-500/20 text-emerald-500' :
            scenarios.overallScore >= 2 ? 'bg-amber-500/20 text-amber-500' :
            'bg-red-500/20 text-red-500'
          }`}>
            {getScoreLabel(scenarios.overallScore)}
          </span>
          {expanded ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
        </div>
      </button>

      {expanded && (
        <>
          {/* Base Case Reference */}
          <div className="mb-4 p-3 bg-slate-800/50 rounded-lg flex justify-between items-center">
            <span className="text-slate-400">Base Case Monthly Cash Flow</span>
            <span className={`font-mono font-bold ${scenarios.base.cashFlow >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
              {formatCurrency(scenarios.base.cashFlow)}/mo
            </span>
          </div>

          {/* Scenario Cards */}
          <div className="space-y-3">
            {scenarioList.map((scenario, idx) => (
              <div 
                key={idx}
                className={`p-4 rounded-lg border ${
                  scenario.survives 
                    ? 'bg-slate-800/30 border-slate-700' 
                    : 'bg-red-500/10 border-red-500/30'
                }`}
                data-testid={`stress-scenario-${idx}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      {scenario.survives ? (
                        <Shield className="h-4 w-4 text-emerald-500" />
                      ) : (
                        <AlertTriangle className="h-4 w-4 text-red-500" />
                      )}
                      <span className="font-semibold text-white">{scenario.name}</span>
                    </div>
                    <p className="text-sm text-slate-400 mt-1">{scenario.description}</p>
                  </div>
                  <div className="text-right">
                    <div className={`font-mono font-bold ${scenario.cashFlow >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                      {formatCurrency(scenario.cashFlow)}/mo
                    </div>
                    <div className="text-xs text-slate-500">
                      {scenario.impact >= 0 ? '+' : ''}{formatCurrency(scenario.impact)} vs base
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Modeled reserve exposure */}
          {scenarios.cashReserveNeeded > 0 && (
            <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-amber-500 mt-0.5" />
                <div>
                  <div className="font-semibold text-amber-500">Modeled Reserve Exposure</div>
                  <p className="text-sm text-slate-300 mt-1">
                    This stress scenario models up to{' '}
                    <span className="font-mono font-bold text-amber-500">
                      {formatCurrency(scenarios.cashReserveNeeded)}
                    </span>{' '}
                    of potential negative cash flow over 12 months. Compare this
                    estimate with your own assumptions and reserve policy.
                  </p>
                </div>
              </div>
            </div>
          )}

          <p className="mt-4 text-xs text-slate-500">
            Scenario outputs are estimates for informational purposes only and are
            not financial or investment advice. Verify assumptions and consult
            qualified professionals before making decisions.
          </p>

          {/* How It's Calculated */}
          <div className="mt-4 text-xs text-slate-500">
            <details>
              <summary className="cursor-pointer hover:text-slate-400">How stress tests are calculated</summary>
              <div className="mt-2 p-3 bg-slate-800/50 rounded-lg space-y-1">
                <p>• <strong>Rate Shock:</strong> Mortgage recalculated at current rate + 2%</p>
                <p>• <strong>High Vacancy:</strong> Vacancy rate increased to 20%</p>
                <p>• <strong>Major Repair:</strong> $15,000 expense spread over 12 months</p>
                <p>• <strong>Market Downturn:</strong> Rent -15%, vacancy +10%</p>
                <p>• <strong>Perfect Storm:</strong> All adverse conditions combined</p>
                <p className="mt-2">The model marks a scenario as within threshold if monthly cash flow stays above -$200 to -$500.</p>
              </div>
            </details>
          </div>
        </>
      )}
    </div>
  );
}
